import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { setI18nLocale } from '../i18n'
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, isRTL, getLocaleConfig, type LocaleConfig } from '../i18n/locales'

export const useLocaleStore = defineStore('locale', () => {
  // Initialize from localStorage or detect from browser
  const storedLocale = localStorage.getItem('locale')
  const initialLocale = storedLocale || detectBrowserLocale() || DEFAULT_LOCALE

  const locale = ref<string>(initialLocale)

  // Track loading state for lazy loading
  const loading = ref(false)

  // Computed properties
  const currentLocaleConfig = computed<LocaleConfig | undefined>(() =>
    getLocaleConfig(locale.value)
  )

  const isRTLMode = computed(() => isRTL(locale.value))

  const availableLocales = computed(() => SUPPORTED_LOCALES)

  // Detect browser/OS preferred language
  function detectBrowserLocale(): string | null {
    const browserLocales = navigator.languages || [navigator.language]

    for (const browserLocale of browserLocales) {
      // Exact match
      const exact = SUPPORTED_LOCALES.find(l => l.code === browserLocale)
      if (exact) return exact.code

      // Base language match (e.g., 'de-AT' -> 'de')
      const baseCode = browserLocale.split('-')[0]
      const base = SUPPORTED_LOCALES.find(l => l.code === baseCode)
      if (base) return base.code

      // Special case: any Chinese variant -> zh or zh-TW
      if (baseCode === 'zh') {
        if (browserLocale.includes('TW') || browserLocale.includes('Hant')) {
          return 'zh-TW'
        }
        return 'zh'
      }

      // Special case: Spanish variants -> es or es-419
      if (baseCode === 'es') {
        // Latin American Spanish codes
        const latamRegions = ['MX', 'AR', 'CO', 'CL', 'PE', 'VE', '419']
        if (latamRegions.some(region => browserLocale.includes(region))) {
          return 'es-419'
        }
        if (browserLocale !== 'es-ES' && browserLocale.includes('-')) {
          // Most non-Spain Spanish speakers prefer Latin American Spanish
          return 'es-419'
        }
        return 'es'
      }

      // Special case: Portuguese variants -> pt or pt-BR
      if (baseCode === 'pt') {
        if (browserLocale.includes('BR')) {
          return 'pt-BR'
        }
        return 'pt'
      }
    }

    return null
  }

  // Apply locale and RTL direction
  async function applyLocale() {
    loading.value = true
    try {
      await setI18nLocale(locale.value)

      // Apply RTL direction to document
      const dir = isRTL(locale.value) ? 'rtl' : 'ltr'
      document.documentElement.dir = dir
      document.documentElement.lang = locale.value
    } finally {
      loading.value = false
    }
  }

  // Set locale and persist
  async function setUserLocale(newLocale: string) {
    if (!SUPPORTED_LOCALES.find(l => l.code === newLocale)) {
      console.warn(`[Locale] Unsupported locale: ${newLocale}`)
      return
    }

    locale.value = newLocale
    localStorage.setItem('locale', newLocale)
    await applyLocale()
  }

  // Initialize
  async function init() {
    await applyLocale()
  }

  return {
    locale,
    loading,
    currentLocaleConfig,
    isRTLMode,
    availableLocales,
    setUserLocale,
    detectBrowserLocale,
    init,
  }
})
